import { create } from "zustand";
import { devtools } from "zustand/middleware";
import firebase from "api/firebase/firebase";
import GeoIdAPI from "utils/GeoIdAPI";
import { toast } from "react-toastify";
import { setUser as setSentryUser } from "api/sentry";

// The signed-in user and their personal (user-owned) boundaries. Replaced the
// Redux `user` domain, which held this as an Immutable Map (`fromJS()`) —
// state is plain JS now:
//   loading:       boolean — true until the first authenticate/logout resolves
//   authenticated: boolean — a (possibly anonymous) firebase user is signed in
//   boundaries:    { [boundaryId]: boundary } — the user's own boundaries
//   user:          { uid, email, language, displayName } | null
//
// The reducer also declared an `areas: {}` field: no action handler ever wrote
// it and nothing ever read it, so it is dropped.
//
// CROSS-DOMAIN WRITE (Ground Rule 4): the redux `addUserBoundary` thunk
// dispatched the boundaries domain's SELECT_BOUNDARY after a successful save.
// A store must not write another domain, so `addUserBoundary` RETURNS the new
// boundary id and the caller performs the select — see
// `hooks/useAddUserBoundary.js`, the single place that owns that pairing.
//
// REFERENCE-STABILITY NOTE (verified against the installed immutable 5.1.5):
// `Map.delete` of an absent key returns the SAME map, and the reducer's
// `state.merge({ boundaries: sameRef })` then short-circuited to the SAME
// state — so a no-op delete never re-rendered `boundaries` consumers. A naive
// spread would mint a new object every time, so `deleteUserBoundary` and
// `updateUserBoundary` bail out before `set()` when the boundary is unknown.
// (`existing.merge(identicalFields)` also short-circuited; that path is not
// reproduced — an edit dialog submitting byte-identical values is not worth a
// deep compare, and it only costs one extra render.)
const initialState = {
  loading: true,
  authenticated: false,
  boundaries: {},
  user: null,
  // Anonymous (?embed) sessions are authenticated but have no real identity.
  isAnonymous: false,
};

const geoIdApi = new GeoIdAPI();

// Ported from the redux `sendToFirebase` helper. Returns the new boundary id,
// or undefined when the save failed (the caller must not select it then).
const saveBoundary = async (
  set,
  get,
  userId,
  boundaryName,
  geoJson,
  areaId,
  geoId,
  description,
) => {
  try {
    const { boundaryId, geojsonSrc } = await firebase.addUserBoundary(
      userId,
      boundaryName,
      geoJson,
      areaId,
      geoId,
      description,
    );
    set(
      {
        boundaries: {
          ...get().boundaries,
          [boundaryId]: {
            geoJson,
            name: boundaryName,
            geojsonSrc,
            areaId,
            geoId,
            description: description || "",
          },
        },
      },
      false,
      "user/addBoundary",
    );
    return boundaryId;
  } catch (error) {
    // Without this, a failed boundary save (e.g. the updateBoundaryGeoJson
    // callable rejecting) becomes an unhandled promise rejection.
    console.error("Failed to save user boundary", error);
    toast.error(`Failed to save boundary: ${error.message}`, {
      autoClose: 8000,
    });
    return undefined;
  }
};

const useUserStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // Ported from the `authenticate` thunk (AUTHENTICATE_START immediately
      // followed by AUTHENTICATE_SUCCESS — synchronous, no request in between).
      // Only these fields are kept: the firebase user object carries far more,
      // and the reducer stored the uid/email/language/displayName subset.
      //
      // `isAnonymous` is the one addition. Login's onAuthStateChanged hands
      // anonymous sessions here too, so this is the only place that knows the
      // difference, and GeoID registration needs it.
      authenticate: (authResponse) => {
        const { uid, email, language, displayName, isAnonymous } = authResponse;
        // Id only (no email): lets Sentry count users per issue — every
        // frontend issue showed "0 users" without it.
        setSentryUser(uid ? { id: uid } : null);
        set({ loading: true }, false, "user/authenticateStart");
        set(
          {
            authenticated: true,
            user: { uid, email, language, displayName },
            isAnonymous: Boolean(isAnonymous),
            loading: false,
          },
          false,
          "user/authenticateSuccess",
        );
      },

      // Ported from the `logout` thunk. The thunk had no error handling: a
      // rejected signOut left an unhandled rejection and never dispatched
      // USER_LOGOUT. The rejection is now logged; the state is still only
      // cleared on a successful sign-out (staying signed in beats showing a
      // signed-out UI against a live firebase session).
      logout: async () => {
        try {
          await firebase.signOut();
        } catch (error) {
          console.error("Failed to sign out", error);
          return;
        }
        set({ ...initialState, loading: false }, false, "user/logout");
        setSentryUser(null);

        // Remove embed parameter from URL after logout to show landing page
        const url = new URL(window.location.href);
        if (url.searchParams.has("embed")) {
          url.searchParams.delete("embed");
          window.location.href = url.toString();
        }
      },

      // Clears the local session WITHOUT the URL rewrite and reload `logout`
      // performs. Login signs a user out when their email domain is not
      // allowed; that must not leave `authenticated` and `user.uid` behind,
      // because every read keyed on the stale uid then violates the RTDB rule
      // "$uid === auth.uid" and comes back Permission denied. It must also not
      // navigate: the Login screen stays put to show the domain error.
      clearSession: () => {
        set({ ...initialState, loading: false }, false, "user/clearSession");
        setSentryUser(null);
      },

      // Ported from the `loadUserBoundaries` thunk. The thunk had no .catch():
      // a rejected read became an unhandled rejection. Failures are now logged
      // and leave the previous boundaries in place.
      loadUserBoundaries: async (userId, areaId) => {
        try {
          const boundaries = await firebase.loadUserBoundaries(userId, areaId);
          set(
            { boundaries: boundaries || {} },
            false,
            "user/loadBoundariesSuccess",
          );
        } catch (error) {
          console.error("Failed to load user boundaries", error);
        }
      },

      // Ported from the `addUserBoundary` thunk. Returns the new boundary id
      // (see the cross-domain-write note above), or undefined on failure.
      //
      // The customization config is read here rather than passed in. As a
      // parameter it was wrong at one of the two call sites for the whole life
      // of the feature — `BoundariesSelection` passed `undefined`, so uploaded
      // boundaries could never be registered while drawn ones could — and the
      // bug survived the redux→zustand port. Reading it here makes the two
      // paths agree by construction.
      addUserBoundary: async (
        userId,
        boundaryName,
        geoJson,
        areaId,
        description,
      ) => {
        let geoId;
        let config;
        try {
          config = await firebase.getCustomizationConfig();
        } catch (error) {
          // Registration is optional; a config read failure must not cost the
          // user their boundary.
          console.error("Failed to fetch customization config", error);
        }

        // Registration is for signed-in users only. A geoid is a durable public
        // record, and an anonymous session has no identity to attach one to or
        // to opt in with — its options are not even readable (the RTDB rules
        // deny /users/{uid}/options to anonymous users, which is what made this
        // skip happen by accident, via an error, before it was explicit).
        if (config?.linkedServices?.geoIdEnabled && !get().isAnonymous) {
          // Registration also needs the user to have opted in.
          let options;
          try {
            options = await firebase.getUserOptions(userId);
          } catch (error) {
            // The thunk returned here without saving, so a failed options read
            // silently threw the drawn polygon away. Registration is optional —
            // losing the boundary over it is not a trade worth making, so carry
            // on and just skip registration.
            // warn, not error: getUserOptions already files this with Sentry,
            // including the auth diagnostics; console.error would duplicate it.
            console.warn("Failed to fetch user options", error);
            options = null;
          }

          if (options && options.geoIdOptIn === true) {
            try {
              const response = await geoIdApi.registerGeoJson(geoJson);
              // Only a string is an id. The AgStack version stored whatever
              // came back, so a failure persisted `{errorMessage}` into the
              // id field.
              if (typeof response === "string") {
                geoId = response;
                toast.success(
                  `Polygon registered in the FAO GeoID registry.\nGeoID: ${response}`,
                  { autoClose: 8000 },
                );
              } else {
                toast.warn(
                  `Could not register the polygon in the GeoID registry: ${response?.errorMessage}`,
                  { autoClose: 8000 },
                );
              }
            } catch (error) {
              console.error("Failed to register boundary with GeoID", error);
            }
          }
        }

        return saveBoundary(
          set,
          get,
          userId,
          boundaryName,
          geoJson,
          areaId,
          geoId,
          description,
        );
      },

      // Ported from the `updateUserBoundary` thunk, which fired the firebase
      // write and dispatched immediately without awaiting it — the state
      // update stays optimistic; only the ignored rejection is now handled.
      updateUserBoundary: async (userId, boundaryId, fields) => {
        const request = Promise.resolve().then(() =>
          firebase.updateUserBoundary(userId, boundaryId, fields),
        );

        const boundaries = get().boundaries;
        const existing = boundaries[boundaryId];
        if (existing) {
          set(
            {
              boundaries: {
                ...boundaries,
                [boundaryId]: { ...existing, ...fields },
              },
            },
            false,
            "user/updateBoundary",
          );
        }

        try {
          await request;
        } catch (error) {
          console.error("Failed to update user boundary", error);
        }
      },

      // Ported from the `deleteUserBoundary` thunk (same optimistic shape as
      // updateUserBoundary).
      deleteUserBoundary: async (userId, boundaryId) => {
        const request = Promise.resolve().then(() =>
          firebase.deleteUserBoundary(userId, boundaryId),
        );

        const boundaries = get().boundaries;
        if (boundaryId in boundaries) {
          const next = { ...boundaries };
          delete next[boundaryId];
          set({ boundaries: next }, false, "user/deleteBoundary");
        }

        try {
          await request;
        } catch (error) {
          console.error("Failed to delete user boundary", error);
        }
      },
    }),
    { name: "user" },
  ),
);

// Plain, non-reactive read of the ordered user boundaries, ported from the
// `getOrderedUserBoundaries` reselect selector. The reselect version returned
// an Immutable Map sorted by name; consumers iterated it with
// `.forEach((boundary, boundaryId) => …)`. Plain JS objects have no ordering
// guarantee for a "sorted map", so this returns an ARRAY of boundaries with
// their id folded in — consumers iterate `boundary.id` instead of the second
// forEach argument.
export const computeOrderedUserBoundaries = (boundaries) =>
  Object.entries(boundaries || {})
    .map(([id, boundary]) => ({ ...boundary, id }))
    .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));

export default useUserStore;
